import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TabsComponent, type Tab } from '@circlek-global/lds-components';
import { distinctUntilChanged, map, switchMap } from 'rxjs';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { ListViewComponent } from '~components/list-view/list-view.component';
import { LocationCountLabelComponent } from '~components/location-count-label/location-count-label.component';
import { MapViewComponent } from '~components/map-view/map-view.component';
import { StationDetailsComponent } from '~components/station-details/station-details.component';
import { StationSearchComponent } from '~components/station-search/station-search.component';
import { StationsFilterViewComponent } from '~components/stations-filters/stations-filters-view/stations-filters-view.component';
import { BreakpointObserverService } from '~services/breakpoint-observer.service';
import { StationsService } from '~services/station.service';
import { TranslationService } from '~services/translation.service';
import { DEFAULT_BUSINESS_UNIT } from '~shared/business-unit';
import { StationDataStore } from '~state/station-data/station-data.store';
import { getBuFromQueryParams } from '~utils/business-unit.utils';

enum TabView {
  Map = 'map',
  List = 'list',
}

@Component({
  selector: 'app-station-locator',
  imports: [
    ListViewComponent,
    MapViewComponent,
    TabsComponent,
    StationSearchComponent,
    StationsFilterViewComponent,
    StationDetailsComponent,
    LocationCountLabelComponent,
  ],
  templateUrl: './station-locator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationLocatorComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationService = inject(TranslationService);
  private readonly stationsService = inject(StationsService);
  private readonly stationDataStore = inject(StationDataStore);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly bu$ = this.route.queryParamMap.pipe(map(getBuFromQueryParams), distinctUntilChanged());
  protected readonly TabView = TabView;
  protected readonly tabs = computed<Tab[]>(() => [
    { label: this.translationService.translate('map') },
    { label: this.translationService.translate('list') },
  ]);
  protected readonly bu = toSignal(this.bu$, { initialValue: DEFAULT_BUSINESS_UNIT });
  protected readonly selected = signal<number>(0);
  protected readonly selectedTabLabel = computed(() => this.tabs()[this.selected()]);
  protected readonly activeTab = signal<TabView>(TabView.Map);
  protected readonly isDesktop = computed(() => this.breakpointObserverService.isDesktop());
  protected readonly isFiltersViewOpened = computed(() => this.stationsService.isFiltersViewOpened());
  protected readonly isStationSelected = computed(() => this.stationsService.selectedStation() !== null);
  protected readonly stations = this.stationsService.filteredStations;

  ngOnInit(): void {
    this.bu$
      .pipe(
        switchMap((bu) => this.stationDataStore.loadFuelsAndServices(bu)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  tabSelected(index: number): void {
    this.selected.set(index);
    this.activeTab.set(index === 0 ? TabView.Map : TabView.List);
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.StoreView, {
      store_view: this.getSelectedTabName(index),
    });
  }

  clearSelection(): void {
    this.stationsService.clearSelectedStation();
  }

  private getSelectedTabName(index: number): string {
    return index === 0 ? TabView.Map : TabView.List;
  }
}
